import { useEffect, useState } from 'react';
import type { MetrcFacilityDocument, MetrcProductDocument } from '../types/index';
import { useSsoReauth } from '../contexts/SsoReauthContext';
import { getEndpointErrorMessage, getSafeEndpointMessage } from '../utils/requestErrors';
import { getFacilities } from './useFacilities';
import { getAvailableFacilities, getAvailableProducts } from './useMetrcImports';

// The import wizard state does not survive the Metrc sign-in redirect, so the user restarts from the imports page.
const IMPORTS_PATH = '/imports';
const SIGN_IN_REQUIRED_MESSAGE = 'Metrc sign-in is required to load this list.';

// excludeImportedFacilities (facility-import workflow): list only facilities that can still be imported. The organization's
// already-imported facilities are otherwise merged in so the product workflow can choose them.
export function useMetrcFacilityOptions(
  organizationId?: number,
  marketId?: number,
  restrictToImportedFacilities = false,
  excludeImportedFacilities = false,
) {
  const { handleSsoError } = useSsoReauth();
  const [facilities, setFacilities] = useState<MetrcFacilityDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setFacilities([]);
    setLoadError(null);
    setLoading(Boolean(organizationId && marketId));
    if (!organizationId || !marketId) return () => { active = false; };

    Promise.all([
      restrictToImportedFacilities ? Promise.resolve([]) : getAvailableFacilities({ organizationId, marketId, page: 0, pageSize: 500 }),
      excludeImportedFacilities ? Promise.resolve([]) : getFacilities(organizationId, marketId, 0, 500).catch(() => []),
    ]).then(([available, existing]) => {
      if (!active) return;
      const merged = new Map<string, MetrcFacilityDocument>();
      available.forEach((facility) => {
        const key = String(facility?.id || facility?.license || '');
        if (key) merged.set(key, facility);
      });
      existing.map((facility) => ({
        id: Number(facility?.metrcFacilityId) || Number(facility?.id) || 0,
        dbFacilityId: Number(facility?.id) || null,
        name: facility?.name || '',
        license: facility?.license || '',
      })).filter(facility => facility.id || facility.license).forEach((facility) => {
        const key = String(facility.id || facility.license);
        if (!merged.has(key)) merged.set(key, facility as MetrcFacilityDocument);
      });
      setFacilities(Array.from(merged.values()));
    }).catch((error) => {
      if (!active) return;
      console.error('Failed to load facilities:', error);
      setLoadError(handleSsoError(error, { returnTo: IMPORTS_PATH })
        ? SIGN_IN_REQUIRED_MESSAGE
        : getSafeEndpointMessage(error, 'Failed to load facilities. Please try again.'));
      setFacilities([]);
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [organizationId, marketId, restrictToImportedFacilities, excludeImportedFacilities]);

  return { facilities, loading, loadError };
}

type AvailableProduct = MetrcProductDocument & {
  facilityId?: number;
  dbFacilityId?: number | null;
  selected?: boolean;
};

export function useMetrcProductOptions(
  organizationId: number | undefined,
  marketId: number,
  facilities: MetrcFacilityDocument[],
) {
  const { handleSsoError } = useSsoReauth();
  const [availableProducts, setAvailableProducts] = useState<AvailableProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setAvailableProducts([]);
    setLoadError(null);
    setLoading(Boolean(organizationId && marketId));
    if (!organizationId || !marketId) return () => { active = false; };

    Promise.all(facilities.map(async (facility) => ({
      facilityId: facility.id,
      dbFacilityId: facility.dbFacilityId,
      documents: await getAvailableProducts({
        organizationId,
        marketId,
        metrcFacilityId: facility.id,
        page: 0,
        pageSize: 1000,
        metrcPackageIds: null,
      }),
    }))).then((products) => {
      if (!active) return;
      setAvailableProducts(products.flatMap(item => item.documents.map(document => ({
        ...document,
        facilityId: item.facilityId,
        dbFacilityId: item.dbFacilityId,
        selected: false,
      }))));
    }).catch((error) => {
      if (!active) return;
      setLoadError(handleSsoError(error, { returnTo: IMPORTS_PATH })
        ? SIGN_IN_REQUIRED_MESSAGE
        : getEndpointErrorMessage(error, 'Error loading products'));
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [organizationId, marketId, facilities]);

  return { availableProducts, setAvailableProducts, loading, loadError };
}
